import { EditFormProps } from "./EditFormProps";
import { SaveEditFormResponse } from "./SaveEditFormResponse";
import { useEffect, useId, useState } from "react";
import { useForm } from "react-hook-form";
import { baseUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { ConfirmationModalData } from "@kaltura/mediaspace-shared-ui";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";

/**
 * The logic of the EditForm component.
 *
 * Use this hook together with the EditFormContents component
 * to render an entry edit form with a customized actions panel.
 */
export const useEditForm = ({
    entryIds,
    formData,
    shouldClose = false,
    onSubmitStarted,
    onBeforeSubmit,
    onSubmit,
    onClose,
    closeAfterSubmit = true,
    onlyBasicData = false,
    formId: externalFormId,
    fieldsStructure,
    allowSaveUntouched = false,
}: EditFormProps) => {
    const [modalData, setModalData] = useState<ConfirmationModalData>();
    const [processing, setProcessing] = useState<boolean>(false);
    const internalFormId = useId();
    const formId = externalFormId ?? internalFormId;

    const onSubmitCallback = useEventHandler(onSubmit);
    const onCloseCallback = useEventHandler(onClose);

    const useFormHookMethods = useForm({
        mode: "onChange",
        defaultValues: formData,
    });

    const { dirtyFields, isValid } = useFormHookMethods.formState;

    // When formData changes externally (e.g. after a bulk edit updates entry tags/metadata),
    // reset the form to reflect the new values — but only if the user hasn't made any
    // local changes yet, to avoid discarding in-progress edits.
    // We serialize formData to a stable string so the effect only fires when actual values
    // change, not when the object reference changes on re-renders.
    const formDataKey = JSON.stringify(formData);

    useEffect(() => {
        const hasDirtyFields = Object.keys(dirtyFields).length > 0;
        if (!hasDirtyFields) {
            useFormHookMethods.reset(formData);
        }

        // Don't use `formData` in the deps array, as we're using the string representation (JSON-serialized) of it,
        // as using the object reference will cause the effect to run unnecessarily, because it's not a stable reference.
        // `useFormHookMethods` is a `react-hook-form` stable reference, so there's no need to add it to the deps array.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formDataKey]);

    const canSaveForm = (allowSaveUntouched || Object.keys(dirtyFields).length !== 0) && isValid;

    // check for ready for publish
    const requiredForPublishFields = fieldsStructure
        ?.filter((field) => field.requiredForPublish)
        .map((field) => (field.belongsTo ? `${field.belongsTo}.${field.id}` : field.id));
    const requiredValuesForPublish = useFormHookMethods
        .watch(requiredForPublishFields)
        .filter(Boolean)
        .filter((e) => !(Array.isArray(e) && e.length === 0));
    const readyForPublish = requiredValuesForPublish.length === requiredForPublishFields.length && entryIds.length > 0;

    // cancel all local changes
    const handleCancel = () => {
        useFormHookMethods.reset(formData);
        onCloseCallback();
    };

    // reset all local changes
    const handleReset = () => {
        useFormHookMethods.reset(formData);
    };

    const handleLeave = () => {
        setModalData(undefined);
        handleCancel();
    };

    const handleClose = () => {
        // useFormHookMethods.formState.isDirty compares current values to default values,
        // so it always returns true because we only pass default values for name, description, tags.
        // instead, look at the list of dirty fields.
        if (Object.keys(dirtyFields).length !== 0) {
            // populate the `are-you-sure` modal:
            setModalData({
                yesCallback: () => {
                    if (!isValid) {
                        setModalData(undefined);
                        return;
                    }
                    // only if form is valid
                    onCloseCallback();
                },
                yesButtonLabel: translate("Save Changes"),
                title: translate("Unsaved Changes"),
                text: translate("Changes aren't saved. Are you sure you want to leave without saving them?"),
            });
        }
        else {
            handleCancel();
        }
    };

    const handleSubmitForm = useFormHookMethods.handleSubmit(async (values) => {
        let confirm = true;
        let isEditable = true;

        if (onBeforeSubmit) {
            ({ confirm, isEditable } = await onBeforeSubmit(entryIds, values));
        }

        if (confirm) {
            submitForm(isEditable);
        }
        else {
            handleCancel();
        }
    });

    // save the values
    const submitForm = (isEditable = true) => {
        return useFormHookMethods.handleSubmit(async (values) => {
            setProcessing(true);
            onSubmitStarted?.();

            let response: SaveEditFormResponse;

            try {
                response = await postKmsData(
                    `${baseUrl}/default/media/update`,
                    { entryIds, data: values, onlyBasicData }
                );
            }
            catch (error: unknown) {
                response = { error: String(error), result: [] };
            }

            setProcessing(false);
            onSubmitCallback?.(response, isEditable);

            if (response.error) {
                console.error("Failed to edit entries", response.error);
            }
            else {
                if (closeAfterSubmit) {
                    onCloseCallback();
                }
                else {
                    useFormHookMethods.reset(values);
                }
            }
        })();
    };

    useEffect(() => {
        if (shouldClose) {
            handleClose();
        }
    }, [shouldClose]);

    return {
        useFormHookMethods,
        formId,
        modalData,
        processing,
        canSaveForm,
        readyForPublish,
        handleSubmitForm,
        handleCancel,
        handleLeave,
        handleReset,
    };
};

export type UseEditFormResponse = ReturnType<typeof useEditForm>;
